import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api';
import { ShieldCheck } from '@phosphor-icons/react';

/**
 * OAuth2 callback handler — Path 2 (Cognito Hosted UI redirect).
 * Cognito redirects here with ?code=...&state=...
 * We POST { code, redirect_uri } to /api/auth/callback and the backend
 * exchanges, verifies the id_token, resolves the Radar user, and sets the
 * session cookies. We then refresh the in-context user and navigate home.
 */
export default function CognitoCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { adoptCallbackSession } = useAuth();
  const [error, setError] = useState('');
  const ranOnce = useRef(false);

  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;

    const code = params.get('code');
    const cognitoError = params.get('error_description') || params.get('error');
    if (cognitoError) {
      setError(decodeURIComponent(cognitoError));
      return;
    }
    if (!code) {
      setError('Missing authorization code from Cognito.');
      return;
    }

    const redirectUri = `${window.location.origin}/callback`;
    (async () => {
      try {
        const { data } = await api.post('/auth/callback', { code, redirect_uri: redirectUri });
        // Store access_token + user from the response directly. No /me
        // roundtrip — avoids the third-party cookie pitfall on cross-site
        // deployments.
        adoptCallbackSession(data);
        navigate('/', { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Sign-in failed. Please try again.');
      }
    })();
  }, [params, navigate, adoptCallbackSession]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6" data-testid="cognito-callback-page">
      <div className="w-full max-w-md text-center">
        <ShieldCheck size={48} weight="duotone" className="mx-auto text-[#0D7377] mb-3" />
        <h1 className="text-2xl font-bold tracking-[3px] text-[#1B2A4A] mb-2">RADAR</h1>
        {error ? (
          <>
            <p className="text-sm text-red-600 mb-4" data-testid="callback-error">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="text-sm text-[#0D7377] underline"
              data-testid="callback-back-to-login"
            >
              Back to login
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-[#52525B]">Signing you in…</p>
            <div className="mt-4 mx-auto w-8 h-8 border-2 border-[#0D7377] border-t-transparent rounded-full animate-spin" />
          </>
        )}
      </div>
    </div>
  );
}
